import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { computeJitter, levelToPixel, pixelToLevel, GRID_MIN, GRID_MAX } from '../lib/stakeholderGraphMath.js';

export interface StakeholderGraphPoint {
  id: string;
  name: string;
  power: number;
  interest: number;
  color?: string | null;
}

interface DragState {
  id: string;
  startPower: number;
  startInterest: number;
}

const SIZE = 380;
const LEVELS = [GRID_MIN, GRID_MIN + 1, GRID_MIN + 2, GRID_MIN + 3, GRID_MAX];

/**
 * 2D-Ansicht der Stakeholder (X = Interesse, Y = Power/Macht) als Ergaenzung
 * zur Tabelle. Punkte sind per Maus verschiebbar (window-mousemove/-up analog
 * zum Gantt-Drag in ApTaskGantt) und per Pfeiltasten (Fokus + ArrowUp/Down/
 * Left/Right) bedienbar. Beim Loslassen bzw. Tastendruck wird NUR power/
 * interest gemeldet — die Strategie wird serverseitig aus dem neuen Wertepaar
 * neu berechnet, sofern kein Override gesetzt ist.
 */
export function StakeholderGraph({
  items, onDropPoint,
}: {
  items: StakeholderGraphPoint[];
  onDropPoint: (id: string, pos: { power: number; interest: number }) => void | Promise<void>;
}) {
  const { t } = useTranslation('projects');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<{ power: number; interest: number } | null>(null);

  const jitter = useMemo(
    () => computeJitter(items.map((p) => ({ id: p.id, power: p.power, interest: p.interest }))),
    [items],
  );

  useEffect(() => {
    if (!drag) return;
    const readLevels = (clientX: number, clientY: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { power: drag.startPower, interest: drag.startInterest };
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      return {
        interest: pixelToLevel(x, rect.width),
        power: pixelToLevel(rect.height - y, rect.height),
      };
    };
    const onMove = (e: MouseEvent) => setPreview(readLevels(e.clientX, e.clientY));
    const onUp = (e: MouseEvent) => {
      const next = readLevels(e.clientX, e.clientY);
      const { id, startPower, startInterest } = drag;
      setDrag(null);
      setPreview(null);
      if (next.power !== startPower || next.interest !== startInterest) {
        void onDropPoint(id, next);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, onDropPoint]);

  const startDrag = (e: React.MouseEvent, item: StakeholderGraphPoint) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ id: item.id, startPower: item.power, startInterest: item.interest });
    setPreview({ power: item.power, interest: item.interest });
  };

  const onKeyDown = (e: React.KeyboardEvent, item: StakeholderGraphPoint) => {
    let power = item.power;
    let interest = item.interest;
    if (e.key === 'ArrowUp') power = Math.min(GRID_MAX, power + 1);
    else if (e.key === 'ArrowDown') power = Math.max(GRID_MIN, power - 1);
    else if (e.key === 'ArrowRight') interest = Math.min(GRID_MAX, interest + 1);
    else if (e.key === 'ArrowLeft') interest = Math.max(GRID_MIN, interest - 1);
    else return;
    e.preventDefault();
    if (power !== item.power || interest !== item.interest) {
      void onDropPoint(item.id, { power, interest });
    }
  };

  const quadrantLabel = (key: string, fallback: string) => t(`stakeholderMatrix.quadrants.${key}`, fallback);

  if (items.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
        {t('stakeholderMatrix.empty', 'Keine Stakeholder erfasst.')}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'var(--pm-fg-muted)' }}>
        {t(
          'stakeholders.graphHint',
          'Punkte ziehen (oder fokussieren + Pfeiltasten), um Power/Interest anzupassen. Beim Loslassen wird auf 1–5 gerastert.',
        )}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'var(--pm-fg-subtle)', fontWeight: 600 }}
          >
            {t('stakeholders.power', 'Power')} →
          </Typography>
        </Box>

        <Box>
          <Box
            ref={containerRef}
            data-testid="stakeholder-graph-plot"
            sx={{
              position: 'relative', width: SIZE, height: SIZE,
              border: '1px solid var(--pm-bg-muted)', borderRadius: 1,
              overflow: 'hidden', userSelect: 'none',
            }}
          >
            {/* Quadranten-Hintergrund (2x2), Grenze bei 3 */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
              <QuadrantCell label={quadrantLabel('keepSatisfied', 'Keep satisfied')} align="left" />
              <QuadrantCell label={quadrantLabel('manageClosely', 'Manage closely')} align="right" />
              <QuadrantCell label={quadrantLabel('monitor', 'Monitor')} align="left" />
              <QuadrantCell label={quadrantLabel('keepInformed', 'Keep informed')} align="right" />
            </Box>

            {items.map((item) => {
              const isDragging = drag?.id === item.id;
              const power = isDragging && preview ? preview.power : item.power;
              const interest = isDragging && preview ? preview.interest : item.interest;
              const j = jitter.get(item.id) ?? { dx: 0, dy: 0 };
              const left = levelToPixel(interest, SIZE) + (isDragging ? 0 : j.dx);
              const top = SIZE - levelToPixel(power, SIZE) + (isDragging ? 0 : j.dy);
              return (
                <Box
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  aria-label={item.name}
                  data-testid={`graph-point-${item.id}`}
                  onMouseDown={(e) => startDrag(e, item)}
                  onKeyDown={(e) => onKeyDown(e, item)}
                  sx={{
                    position: 'absolute', left, top, transform: 'translate(-50%, -50%)',
                    width: 14, height: 14, borderRadius: '50%',
                    background: `var(--pm-${item.color ?? 'k02'})`,
                    border: '2px solid var(--pm-bg-base)',
                    boxShadow: isDragging ? '0 0 0 3px var(--pm-fg-subtle)' : '0 0 0 1px var(--pm-fg-subtle)',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    zIndex: isDragging ? 5 : 1,
                    transition: isDragging ? 'none' : 'left .15s, top .15s',
                    '&:focus-visible': { outline: '2px solid var(--pm-fg-base)', outlineOffset: 2 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap', fontSize: 10, color: 'var(--pm-fg-muted)', pointerEvents: 'none',
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Stack direction="row" sx={{ mt: 0.5 }}>
            {LEVELS.map((lvl) => (
              <Box key={lvl} sx={{ width: SIZE / 5, textAlign: 'center', fontSize: 11, color: 'var(--pm-fg-subtle)', fontWeight: 600 }}>
                {lvl}
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'var(--pm-fg-subtle)', fontWeight: 600 }}>
            {t('stakeholders.interest', 'Interest')} →
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function QuadrantCell({ label, align }: { label: string; align: 'left' | 'right' }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
        p: 0.75, pointerEvents: 'none',
      }}
    >
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', opacity: 0.6, fontWeight: 600, fontSize: 10 }}>
        {label}
      </Typography>
    </Box>
  );
}
